import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export type BookingStepId = 'service' | 'datetime' | 'payment' | 'confirmation';

const STEPS: { id: BookingStepId; label: string }[] = [
  { id: 'service', label: 'Service' },
  { id: 'datetime', label: 'Date & Time' },
  { id: 'payment', label: 'Payment' },
  { id: 'confirmation', label: 'Confirmation' },
];

interface BookingStepIndicatorProps {
  current: BookingStepId;
  className?: string;
}

/**
 * Four-step progress indicator shared across the booking flow.
 * Purely presentational — it never navigates.
 */
export function BookingStepIndicator({ current, className }: BookingStepIndicatorProps) {
  const currentIndex = STEPS.findIndex((s) => s.id === current);

  return (
    <nav
      aria-label="Booking progress"
      className={cn('w-full max-w-3xl mx-auto px-4', className)}
    >
      <ol className="flex items-center">
        {STEPS.map((step, i) => {
          const isDone = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
            <li key={step.id} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-medium transition-colors',
                    isDone && 'bg-primary border-primary text-primary-foreground',
                    isCurrent && 'border-primary text-primary bg-primary/10',
                    !isDone && !isCurrent && 'border-border text-muted-foreground bg-background'
                  )}
                >
                  {isDone ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span
                  className={cn(
                    'text-[11px] sm:text-xs whitespace-nowrap',
                    isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground'
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {step.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-px flex-1 mx-2 -mt-5',
                    i < currentIndex ? 'bg-primary' : 'bg-border'
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default BookingStepIndicator;
